import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Globe,
  Search,
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Users,
  Package,
  Settings,
  Eye,
  EyeOff,
  Download,
  RefreshCw,
} from 'lucide-react';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { adminApi, type OrganizationData } from '@/services/adminApi';
import { formatDistanceToNow } from 'date-fns';
import { logError } from '@/utils/errorHandler';

export function OrganizationManagement() {
  const { hasPermission } = useAdminPermissions();
  const queryClient = useQueryClient();
  const [filteredOrgs, setFilteredOrgs] = useState<OrganizationData[]>([]);
  const [filter, setFilter] = useState({
    search: '',
    plan: 'all',
    status: 'all',
    industry: 'all',
  });

  // Fetch organizations using React Query
  const {
    data: organizations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => adminApi.getOrganizations(),
    refetchInterval: 60000, // Refetch every minute
    retry: 2,
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  // Mutation for updating organization status
  const updateStatusMutation = useMutation({
    mutationFn: ({
      orgId,
      status,
    }: {
      orgId: string;
      status: 'active' | 'inactive' | 'suspended';
    }) => adminApi.updateOrganizationStatus(orgId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      toast.success('Organization status updated successfully');
    },
    onError: error => {
      logError(
        'Failed to update organization status',
        {
          component: 'OrganizationManagement',
          action: 'updateOrganizationStatus',
          metadata: {
            error,
          },
        },
        {
          showToast: true,
          toastMessage: 'Failed to update organization status',
          userFacing: true,
        }
      );
    },
  });

  // Mutation for deleting organization
  const deleteOrganizationMutation = useMutation({
    mutationFn: (orgId: string) => adminApi.deleteOrganization(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      toast.success('Organization deleted successfully');
    },
    onError: error => {
      logError(
        'Failed to delete organization',
        {
          component: 'OrganizationManagement',
          action: 'deleteOrganization',
          metadata: {
            error,
          },
        },
        {
          showToast: true,
          toastMessage: 'Failed to delete organization',
          userFacing: true,
        }
      );
    },
  });

  // Filter organizations whenever organizations data or filter changes
  useEffect(() => {
    let filtered = organizations;

    if (filter.search) {
      filtered = filtered.filter(
        org =>
          org.name.toLowerCase().includes(filter.search.toLowerCase()) ||
          org.industry.toLowerCase().includes(filter.search.toLowerCase()) ||
          org.ownerName.toLowerCase().includes(filter.search.toLowerCase()) ||
          org.billingEmail.toLowerCase().includes(filter.search.toLowerCase())
      );
    }

    if (filter.plan !== 'all') {
      filtered = filtered.filter(org => org.plan === filter.plan);
    }

    if (filter.status !== 'all') {
      filtered = filtered.filter(org => org.status === filter.status);
    }

    if (filter.industry !== 'all') {
      filtered = filtered.filter(org => org.industry === filter.industry);
    }

    setFilteredOrgs(filtered);
  }, [organizations, filter]);

  const handleToggleStatus = async (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (!org) return;

    const newStatus = org.status === 'active' ? 'inactive' : 'active';

    try {
      await updateStatusMutation.mutateAsync({ orgId, status: newStatus });
    } catch {
      // Error already handled in mutation
    }
  };

  const handleDeleteOrganization = async (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    if (!org) return;

    if (
      !confirm(
        `Are you sure you want to delete "${org.name}"? This action cannot be undone and will permanently remove all data associated with this organization.`
      )
    ) {
      return;
    }

    try {
      await deleteOrganizationMutation.mutateAsync(orgId);
    } catch {
      // Error already handled in mutation
    }
  };

  const handleExportData = async () => {
    try {
      toast.info('Preparing export...');

      // Create CSV content
      const csvHeaders = [
        'Name',
        'Plan',
        'Status',
        'Users',
        'Products',
        'Monthly Revenue',
        'Industry',
        'Country',
        'Owner',
        'Created At',
        'Last Active',
      ];

      const csvRows = filteredOrgs.map(org => [
        org.name,
        org.plan,
        org.status,
        org.userCount.toString(),
        org.productCount.toString(),
        `$${org.monthlyRevenue}`,
        org.industry,
        org.country,
        org.ownerName,
        new Date(org.createdAt).toLocaleDateString(),
        new Date(org.lastActive).toLocaleDateString(),
      ]);

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `organizations_export_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Organizations data exported successfully');
    } catch (error) {
      logError(
        'Failed to export organization data',
        {
          component: 'OrganizationManagement',
          action: 'exportData',
          metadata: {
            organizationCount: filteredOrgs.length,
            error,
          },
        },
        {
          showToast: true,
          toastMessage: 'Failed to export data',
          userFacing: true,
        }
      );
    }
  };

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'free':
        return <Badge className="bg-gray-100 text-gray-800">Free</Badge>;
      case 'basic':
        return <Badge className="bg-blue-100 text-blue-800">Basic</Badge>;
      case 'professional':
        return <Badge className="bg-purple-100 text-purple-800">Professional</Badge>;
      case 'enterprise':
        return <Badge className="bg-yellow-100 text-yellow-800">Enterprise</Badge>;
      default:
        return <Badge variant="secondary">{plan}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800">Suspended</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const industries = [...new Set(organizations.map(org => org.industry))];

  if (!hasPermission('admin.organizations.view')) {
    return (
      <AdminLayout title="Access Denied">
        <div className="py-8 text-center">
          <p>You don't have permission to manage organizations.</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout
        title="Organization Management"
        description="Manage all organizations, plans, and settings"
      >
        <div className="py-8 text-center">
          <p className="mb-4 text-red-600">Failed to load organizations: {error.message}</p>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="mr-2 size-4" />
            Retry
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Organization Management"
      description="Manage all organizations, plans, and settings"
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search organizations..."
                value={filter.search}
                onChange={e => setFilter({ ...filter, search: e.target.value })}
                className="w-64 pl-10"
              />
            </div>

            <Select
              value={filter.plan}
              onValueChange={value => setFilter({ ...filter, plan: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.status}
              onValueChange={value => setFilter({ ...filter, status: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.industry}
              onValueChange={value => setFilter({ ...filter, industry: value })}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map(industry => (
                  <SelectItem key={industry} value={industry}>
                    {industry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="mr-2 size-4" />
              Refresh
            </Button>

            <Button variant="outline" onClick={handleExportData}>
              <Download className="mr-2 size-4" />
              Export
            </Button>

            {hasPermission('admin.organizations.create') && (
              <Button onClick={() => toast.info('Create organization feature coming soon!')}>
                <Plus className="mr-2 size-4" />
                Add Organization
              </Button>
            )}
          </div>
        </div>

        {/* Organization Statistics */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Organizations</CardTitle>
              <Globe className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{organizations.length}</div>
              <p className="text-xs text-muted-foreground">
                {filteredOrgs.length !== organizations.length && `${filteredOrgs.length} filtered`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Organizations</CardTitle>
              <Eye className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {organizations.filter(o => o.status === 'active').length}
              </div>
              <p className="text-xs text-muted-foreground">
                {(
                  (organizations.filter(o => o.status === 'active').length / organizations.length) *
                  100
                ).toFixed(1)}
                % of total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {organizations.reduce((sum, org) => sum + org.userCount, 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                Avg{' '}
                {Math.round(
                  organizations.reduce((sum, org) => sum + org.userCount, 0) / organizations.length
                )}{' '}
                per org
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Products</CardTitle>
              <Package className="size-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {organizations.reduce((sum, org) => sum + org.productCount, 0).toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                Monthly Revenue: $
                {organizations.reduce((sum, org) => sum + org.monthlyRevenue, 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Organizations Table */}
        <Card>
          <CardHeader>
            <CardTitle>Organizations ({filteredOrgs.length})</CardTitle>
            <CardDescription>Manage organization settings, plans, and status</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <RefreshCw className="size-4 animate-spin" />
                        <span>Loading organizations...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredOrgs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center">
                      No organizations found matching your criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrgs.map(org => (
                    <TableRow key={org.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{org.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {org.industry} • {org.country}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Owner: {org.ownerName}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getPlanBadge(org.plan)}</TableCell>
                      <TableCell>{getStatusBadge(org.status)}</TableCell>
                      <TableCell>{org.userCount}</TableCell>
                      <TableCell>{org.productCount.toLocaleString()}</TableCell>
                      <TableCell>
                        <span className="font-medium">${org.monthlyRevenue}/mo</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(org.lastActive), {
                            addSuffix: true,
                          })}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="size-8 p-0">
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => toast.info('Edit organization feature coming soon!')}
                            >
                              <Edit className="mr-2 size-4" />
                              Edit Organization
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                toast.info('Organization settings feature coming soon!')
                              }
                            >
                              <Settings className="mr-2 size-4" />
                              Manage Settings
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleToggleStatus(org.id)}
                              disabled={updateStatusMutation.isPending}
                            >
                              {org.status === 'active' ? (
                                <>
                                  <EyeOff className="mr-2 size-4" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <Eye className="mr-2 size-4" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteOrganization(org.id)}
                              className="text-red-600"
                              disabled={deleteOrganizationMutation.isPending}
                            >
                              <Trash2 className="mr-2 size-4" />
                              Delete Organization
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
